import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './css/PersonalInfo.css';
import { useAuth } from './common/AuthContext';
import { authFetch } from './common/authFetch';
import { getGroups } from './common/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = [
  { id: 'google', name: 'Google', icon: '/images/google-icon.svg' }
];

function PersonalInfo() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Social accounts state
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [hasPassword, setHasPassword] = useState(true);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [imageInput, setImageInput] = useState('');
  const [updatingProfile, setUpdatingProfile] = useState(false);

  // Email state
  const [email, setEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);

  const fetchLinkedAccounts = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/auth/oauth/accounts`);
      const data = await response.json();
      if (data.success) {
        setLinkedAccounts(data.accounts || []);
        setHasPassword(data.hasPassword !== false);
      }
    } catch (error) {
      // Silently fail - accounts section will show unlinked state
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/user/profile`);
      const data = await response.json();
      if (data.success) {
        setDisplayName(data.display_name || '');
        setProfileImage(data.profile_image || '');
      }
    } catch (error) {
      // Silently fail
    }
  }, []);

  const fetchEmail = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/user/email`);
      const data = await response.json();
      if (data.success) {
        setEmail(data.email || '');
      }
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchLinkedAccounts();
    fetchEmail();
    fetchProfile();
  }, [fetchLinkedAccounts, fetchEmail, fetchProfile]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('personalInfo.invalidImageFormat'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('personalInfo.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 200;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // 정사각형 크롭 (중앙 기준)
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        setImageInput(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async () => {
    setUpdatingProfile(true);
    try {
      const body = { display_name: nameInput };
      if (imageInput !== profileImage) {
        body.profile_image = imageInput;
      }
      const response = await authFetch(`${API_URL}/api/v1/user/profile`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        setDisplayName(nameInput);
        if (imageInput !== profileImage) setProfileImage(imageInput);
        setEditingProfile(false);
        toast.success(t('personalInfo.profileUpdated'));
      } else {
        toast.error(data.message || t('personalInfo.profileUpdateFailed'));
      }
    } catch (error) {
      toast.error(t('personalInfo.profileUpdateFailed'));
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!emailInput || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      toast.error(t('personalInfo.invalidEmail'));
      return;
    }
    setUpdatingEmail(true);
    try {
      const response = await authFetch(`${API_URL}/api/v1/user/email`, {
        method: 'PUT',
        body: JSON.stringify({ email: emailInput })
      });
      const data = await response.json();
      if (data.success) {
        setEmail(emailInput);
        setEditingEmail(false);
        toast.success(t('personalInfo.emailUpdated'));
      } else {
        toast.error(data.message || t('personalInfo.emailUpdateFailed'));
      }
    } catch (error) {
      toast.error(t('personalInfo.emailUpdateFailed'));
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleLinkProvider = (provider) => {
    window.location.href = `${API_URL}/api/v1/auth/oauth/${provider}`;
  };

  const handleUnlinkProvider = async (provider) => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/auth/oauth/link/${provider}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setLinkedAccounts(prev => prev.filter(a => a.provider !== provider));
        toast.success(t('personalInfo.unlinkSuccess'));
      } else {
        toast.error(data.message || t('personalInfo.unlinkFailed'));
      }
    } catch (error) {
      toast.error(t('personalInfo.unlinkFailed'));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast.error(t('personalInfo.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8 || newPassword.length > 128) {
      toast.error(t('personalInfo.passwordLength'));
      return;
    }

    setChangingPassword(true);
    try {
      const response = await authFetch(`${API_URL}/api/v1/auth/password`, {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(t('personalInfo.passwordChanged'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        // Tokens invalidated server-side, re-login needed
        logout();
        navigate('/');
      } else {
        toast.error(data.message || t('personalInfo.passwordChangeFailed'));
      }
    } catch (error) {
      toast.error(t('personalInfo.passwordChangeFailed'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (hasPassword && !deletePassword) return;
    if (!hasPassword && deleteConfirmInput !== user?.username) return;

    setDeletingAccount(true);
    try {
      const response = await authFetch(`${API_URL}/api/v1/auth/account`, {
        method: 'DELETE',
        body: JSON.stringify(hasPassword
          ? { password: deletePassword }
          : { confirmUsername: deleteConfirmInput })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(t('personalInfo.accountDeleted'));
        logout();
        navigate('/');
      } else {
        toast.error(data.message || t('personalInfo.deleteAccountFailed'));
      }
    } catch (error) {
      toast.error(t('personalInfo.deleteAccountFailed'));
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="personal-info-container">
      <h2>{t('personalInfo.title')}</h2>

      <div className="form-group">
        <label>{t('personalInfo.id')}: {user?.username}</label>
      </div>

      <div className="form-group">
        <label>{t('personalInfo.join_group')}:</label>
        <div className="group-list">
          {getGroups().map((group, index) => (
            <div key={index} className="group-item">
              {group.group_name}
            </div>
          ))}
        </div>
      </div>

      {/* Profile Section */}
      <div className="section-divider" />
      <h3>{t('personalInfo.profile')}</h3>
      <div className="form-group">
        {!editingProfile ? (
          <div className="profile-display">
            <div className="profile-photo-display">
              {profileImage ? (
                <img src={profileImage} alt="profile" className="profile-photo-img" />
              ) : (
                <div className="profile-photo-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="profile-info-display">
              <span className="profile-name-text">{displayName || '-'}</span>
              <button
                className="btn-link"
                onClick={() => { setNameInput(displayName); setImageInput(profileImage); setEditingProfile(true); }}
                style={{ padding: '4px 12px', fontSize: '0.85rem' }}
              >
                {t('personalInfo.edit')}
              </button>
            </div>
          </div>
        ) : (
          <div className="profile-edit">
            <div className="profile-photo-edit">
              <div className="profile-photo-preview">
                {imageInput ? (
                  <img src={imageInput} alt="preview" className="profile-photo-img" />
                ) : (
                  <div className="profile-photo-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                )}
              </div>
              <div className="profile-photo-buttons">
                <label className="btn-link profile-upload-btn">
                  {t('personalInfo.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
                {imageInput && (
                  <button
                    className="btn-unlink"
                    onClick={() => setImageInput('')}
                    style={{ padding: '4px 12px', fontSize: '0.85rem' }}
                  >
                    {t('personalInfo.removePhoto')}
                  </button>
                )}
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>{t('personalInfo.displayName')}</label>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder={t('personalInfo.displayNamePlaceholder')}
                maxLength={50}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                className="btn-change-password"
                onClick={handleUpdateProfile}
                disabled={updatingProfile}
                style={{ flex: 1 }}
              >
                {t('personalInfo.save')}
              </button>
              <button
                className="btn-delete-cancel"
                onClick={() => setEditingProfile(false)}
                style={{ flex: 1 }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Section */}
      <div className="section-divider" />
      <h3>{t('personalInfo.email')}</h3>
      <div className="form-group">
        {!editingEmail ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{email || '-'}</span>
            <button
              className="btn-link"
              onClick={() => { setEmailInput(email); setEditingEmail(true); }}
              style={{ padding: '4px 12px', fontSize: '0.85rem' }}
            >
              {t('personalInfo.edit')}
            </button>
          </div>
        ) : (
          <div>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder={t('personalInfo.emailPlaceholder')}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                className="btn-change-password"
                onClick={handleUpdateEmail}
                disabled={updatingEmail}
                style={{ flex: 1 }}
              >
                {t('personalInfo.save')}
              </button>
              <button
                className="btn-delete-cancel"
                onClick={() => setEditingEmail(false)}
                style={{ flex: 1 }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Section */}
      <div className="section-divider" />
      <h3>{t('personalInfo.changePassword')}</h3>
      <form onSubmit={handleChangePassword} className="password-change-form">
        <div className="form-group">
          <label>{t('personalInfo.currentPassword')}</label>
          {hasPassword ? (
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          ) : (
            <input
              type="password"
              disabled
              placeholder={t('personalInfo.socialFirstPasswordHint')}
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)', fontStyle: 'italic' }}
            />
          )}
        </div>
        <div className="form-group">
          <label>{t('personalInfo.newPassword')}</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            maxLength={128}
          />
        </div>
        <div className="form-group">
          <label>{t('personalInfo.confirmNewPassword')}</label>
          <input
            type="password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            required
            minLength={8}
            maxLength={128}
          />
        </div>
        <button type="submit" className="btn-change-password" disabled={changingPassword}>
          {t('personalInfo.changePassword')}
        </button>
      </form>

      {/* Social Accounts Section */}
      <div className="section-divider" />
      <h3>{t('personalInfo.socialAccounts')}</h3>
      <div className="social-accounts-section">
        {loadingAccounts ? (
          <p>...</p>
        ) : (
          PROVIDERS.map(provider => {
            const linked = linkedAccounts.find(a => a.provider === provider.id);
            return (
              <div key={provider.id} className="social-account-row">
                <div className="social-account-info">
                  <img
                    src={provider.icon}
                    alt={provider.name}
                    className="social-icon-small"
                  />
                  <span className="provider-name">{provider.name}</span>
                  {linked && (
                    <span className="provider-email">{linked.provider_email}</span>
                  )}
                </div>
                {linked ? (
                  <button
                    className="btn-unlink"
                    onClick={() => handleUnlinkProvider(provider.id)}
                  >
                    {t('personalInfo.unlink')}
                  </button>
                ) : (
                  <button
                    className="btn-link"
                    onClick={() => handleLinkProvider(provider.id)}
                  >
                    {t('personalInfo.link')}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Delete Account Section */}
      <div className="section-divider" />
      <div className="danger-zone">
        <h3>{t('personalInfo.deleteAccount')}</h3>
        <p className="delete-warning">{t('personalInfo.deleteWarning')}</p>
        {!showDeleteConfirm ? (
          <button
            className="btn-delete-account"
            onClick={() => setShowDeleteConfirm(true)}
          >
            {t('personalInfo.deleteAccount')}
          </button>
        ) : (
          <div className="delete-confirm-section">
            <p>{t('personalInfo.deleteConfirm')}</p>
            {hasPassword ? (
              <div className="form-group">
                <label>{t('password')}</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              </div>
            ) : (
              <div className="form-group">
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {t('personalInfo.deleteAccountSocialHint')}
                </p>
                <label>{t('personalInfo.enterUsername')}</label>
                <input
                  type="text"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder={user?.username}
                />
              </div>
            )}
            <div className="delete-confirm-buttons">
              <button
                className="btn-delete-confirm"
                onClick={handleDeleteAccount}
                disabled={deletingAccount || (hasPassword ? !deletePassword : deleteConfirmInput !== user?.username)}
              >
                {t('personalInfo.confirmDelete')}
              </button>
              <button
                className="btn-delete-cancel"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword('');
                  setDeleteConfirmInput('');
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonalInfo;
